import {
  bridgeLoanSubTypes,
  isEmptyObject,
  LoanSubType,
  LoanType,
  parseDate,
  sfrPPPMapping,
  termLoanSubTypes,
} from '@roc/feature-utils';
import { format, isValid } from 'date-fns';
import { LoanApplicationBorrowerEntityBorrower } from './loanApplicationTypes';
import { US_TREASURY_5_YR_INDEX } from '@roc/feature-loans';
import { mapAmortization } from 'libs/feature-loans/src/loanSubmission/stores/common/pricerSummaryHelper';

export const MIXED_USE = 'Mixed Use';
export const MULTIFAMILY_5_PLUS = '5Plus Unit Multifamily';


export const getUnitsByPropertyType = value => {

  switch (value) {
    case 'Single Family':
    case 'Townhome':
    case 'Planned Unit Development':
    case 'Condominium':
      return 1;
    case 'Duplex':
      return 2;
    case 'Triplex':
      return 3;
    case 'Quadruplex':
      return 4;
    case MULTIFAMILY_5_PLUS:
      return 5;
    case MIXED_USE:
      return 2;
    default:
      return 0;
  }
};

export const mapSinglePropertyLoanPayload = (
  currentLoan,
  entity,
  borrowers,
  properties,
  quote,
  userStore,
  attorneyData
) => {
  const singleProperty = mapSingleProperty(properties);
  return {
    formInformation: JSON.stringify(mapLoanFormInformation(currentLoan, entity, borrowers, singleProperty, quote, userStore, attorneyData)),
    borrowerEntity: mapBorrowerEntity(entity),
    borrowers: mapBorrowers(borrowers),
    loanInsuranceData: {},
    titleCompany: {},
    isSubmitted: true,
    preferredTitle: '',
    loanPricingData: mapLoanPricingData(quote),
    emailsForCreditBackgroundCheckRequests: '',
    draftLoanId: null,
    investorId: quote.pricerLoanTerms.investorId,
    lenderOriginatorId: null,
    fastTrack: false,
    fastTrackOptions: {},
    broker: null,
    loanLenderDetails: mapLoanLenderDetails(quote),
    firstTimeBorrowerFreeAppraisal: false,
    freeAppraisalPromotion: false,
    submittedFromPortal: currentLoan.submittedFromPortal,
  };
};

const getLoanType = (loanSubType: LoanSubType) => {
  if (bridgeLoanSubTypes.includes(loanSubType)) {
    return LoanType.RESIDENTIAL_BRIDGE;
  } else if (termLoanSubTypes.includes(loanSubType)) {
    return LoanType.RESIDENTIAL_TERM;
  }
  return null;
};

export const mapBorrowerEntity = (
  data
) => {
  if (!isEmptyObject(data)) {
    return {
      borrowerEntity: {
        borrowerEntityId: data.borrowerEntityId,
        name: data?.company?.name ?? data.name,
        ein: data.ein,
        type: data.type,
        operatingAgreementDate: parseDate(data.operatingAgreementDate).toString(),
        address: data.company?.address ?? data.address?.address,
        city: data?.company?.address ?? data.address?.city,
        zipCode: data?.company?.zipCode ?? data.address?.zip,
        streetName: data?.company?.streetName ?? data.address?.streetName,
        streetNumber: data?.company?.streetNumber ?? data.address?.streetNumber,
        state: data?.company?.state ?? data.address?.state,
        loanApplicationUuid: null
      }
    }
  }
  return {};
};

export const mapBorrowers = (borrowers) => {
  return borrowers.map(borrower => ({
    borrowerId: borrower.borrowerId,
    firstName: borrower.firstName,
    lastName: borrower.lastName,
    fullName: `${borrower.firstName} ${borrower.lastName}`,
    emailAddress: borrower.emailAddress,
    cellPhone: borrower.cellPhone,
    personalGuarantor: borrower.personalGuarantor,
    pctOwnership: borrower.pctOwnership,
    citizenshipStatus: borrower.citizenshipStatus,
    rentalExperience: borrower.rentalExperience,
    ficoProvidedAtOrigination: borrower.ficoProvidedAtOrigination,
    experienceProvidedAtOrigination: borrower.experienceProvidedAtOrigination,
    leadSfId: borrower.leadSfId,
    leadId: borrower.leadId,
    leadSource: borrower.leadSource,
    leadSourceGroup: borrower.leadSourceGroup,
    bankruptcy: borrower.bankruptcy,
    bankruptcyDate: borrower.bankruptcyDate,
    foreclosure: borrower.foreclosure,
    foreclosureDate: borrower.foreclosureDate,
  }));
};

const mapSingleProperty = (propertyData) => {
  return {
    ...propertyData,
    propertyOwnership: null,
    purchaseDate: isValid(propertyData?.purchaseDate)
      ? format(propertyData.purchaseDate, 'MM/dd/yyyy') : null,
    acquisitionDate: isValid(propertyData?.purchaseDate)
      ? format(propertyData.purchaseDate, 'MM/dd/yyyy') : null,
    useCode: propertyData?.propertyType,
    capitalImprovements: propertyData?.renoBudget,
    monthlyGrossRent: propertyData?.monthlyMarketRent,
    units: getUnitsByPropertyType(propertyData?.propertyType),
    acquisitionPrice: propertyData?.purchasePrice,
    noOfUnitLeased: propertyData.unitsOccupied,
    anyDebt: !!propertyData?.outstandingLoanOnTheProperty,
    unitsLeaseType: propertyData.unitsOccupiedHave12MonthsLease,
    unitsOccupiedHave12MonthsLease: propertyData.currentlyOccupied,
    numberOfPropertiesAtSubmission: 1,
    rural: propertyData.rural,
    nsfrLoanPropertyFields: {
      ...propertyData.nsfrLoanPropertyFields,
      acquisitionDate: isValid(propertyData?.purchaseDate)
        ? format(propertyData.purchaseDate, 'MM/dd/yyyy') : null,
      propertyValuation: propertyData?.currentMarketValue,
      acquisitionPrice: propertyData?.purchasePrice,
      propertyType: propertyData?.propertyType,
      annualGrossRent: propertyData?.monthlyMarketRent * 12,
      section8: propertyData?.section8,
      monthlyGrossRent: propertyData?.monthlyMarketRent,
      annualTaxes: propertyData.annualTaxes,
      annualInsurance: propertyData.annualInsurance,
      annualHOA: propertyData.annualHOA,
    },
    extraFields: {
      ...propertyData.extraFields,
      seekingCashOut: propertyData?.cashOut ? 'Y' : 'N',
    },
    loanRecourse: 'Full Recourse',
  };
}

export const mapLoanPricingData = (loanPricingData) => {
  return {
    useSpread: true,
    swapRateAtOrigination: loanPricingData.pricerLoanTerms.swapRate,
    swapRate: loanPricingData.pricerLoanTerms.swapRate,

    spreadRateAtOrigination: loanPricingData.pricerLoanTerms.spreadRate,
    spreadRate: loanPricingData.pricerLoanTerms.spreadRate,

    swapIndex: loanPricingData.pricerLoanTerms.swapIndex
      ? parseInt('' + loanPricingData.pricerLoanTerms.swapIndex)
      : US_TREASURY_5_YR_INDEX,
    swapIndexAtOrigination: loanPricingData.pricerLoanTerms.swapIndex
      ? parseInt('' + loanPricingData.pricerLoanTerms.swapIndex)
      : US_TREASURY_5_YR_INDEX,

    programType: loanPricingData.pricerLoanTerms.selectedLtvRate.programType,
  }
};

export const mapLoanLenderDetails = (quote) => {
  const isInternalLender = quote.borrowerSubmissionFees.isInternalLender;
  return {
    originationPointsTpo: 0,
    originationPointsRoc: 0,
    originationPointsRetail: isInternalLender ? quote.borrowerSubmissionFees.termOriginationPoints : 0,
    buydownPointsTpo: 0,
    buydownPointsRoc: 0,
    buydownPointsRetail: 0,
    processingFee: isInternalLender ? 0 : quote.borrowerSubmissionFees.processingFees,
    rocProcessingFee: 0,
    retailProcessingFee: isInternalLender ? quote.borrowerSubmissionFees.processingFees : 0,
    adminFee: isInternalLender ? 0 : quote.borrowerSubmissionFees.adminFees,
    rocAdminFee: 0,
    retailAdminFee: isInternalLender ? quote.borrowerSubmissionFees.adminFees : 0,
    underwritingFee: isInternalLender ? 0 : quote.borrowerSubmissionFees.underwriterFees,
    rocUnderwritingFee: 0,
    retailUnderwritingFee: isInternalLender ? quote.borrowerSubmissionFees.underwriterFees : 0,
    commitmentFee: isInternalLender ? 0 : quote.borrowerSubmissionFees.commitmentFees,
    rocCommitmentFee: 0,
    retailCommitmentFee: isInternalLender ? quote.borrowerSubmissionFees.commitmentFees : 0,
    lenderBuydownFee: isInternalLender ? 0 : quote.borrowerSubmissionFees.buydownFees,
    rocBuydownFee: 0,
    retailBuydownFee: isInternalLender ? quote.borrowerSubmissionFees.buydownFees : 0,
    rocCustomFees: [],
    lenderCustomFees: [],
    retailCustomFees: []
  }
};

export const mapLoanFormInformation = (currentLoan, entity, borrowers, singleProperty, quote, userStore, attorneyData) => {
  const isInternalLender = quote.borrowerSubmissionFees.isInternalLender;
  return {
    loanType: getLoanType(currentLoan.loanSubType),
    loanSubtype: currentLoan.loanSubType,
    submittedFromPortal: currentLoan.submittedFromPortal,
    attorneyName: attorneyData?.attorneyName,
    attorneyEmail: attorneyData?.attorneyEmail,
    lenderAttorneySelectedName: attorneyData?.attorneyName,
    primaryProperty: singleProperty,
    primaryPropertyState: singleProperty.state,
    propertiesMap: {
      rows: [singleProperty]
    },
    loanBorrowerInformation: {
      loanBorrowers: {}
    },
    rocUnderwritingFees: '',
    amortization: mapAmortization(quote.amortization),
    amortizationLabel: mapAmortization(quote.amortization),
    amount: quote.pricerLoanTerms.amount ?? 0,
    rateType: quote.rateType,
    rate: quote.rate,
    prePaymentPenalty: quote.prePaymentPenalty,
    prepaymentDescription: getPrepaymentPenaltyDescription(quote.prePaymentPenalty),
    totalPremium: quote.pricerLoanTerms.totalPremium,
    rateChargedToBorrower: quote.pricerLoanTerms.rateChargedToBorrower,
    loanToValue: quote.loanToValue,
    loanToValueAtSubmission: quote.loanToValue,
    loanLtv: quote.loanToValue,
    outOfBox: quote.pricerLoanTerms.outOfBox,
    oneTimeYieldSpreadPremium: quote.yieldSpread,
    oneTimeYieldSpreadPremiumAmount: quote.pricerLoanTerms.oneTimeYieldSpreadPremiumAmount,
    monthlyPayment: quote.pricerLoanTerms.monthlyPayment,
    lenderPremium: quote.pricerLoanTerms.lenderPremium,
    acquisitionPrice: quote.pricerLoanTerms.acquisitionPrice,
    investorSalePrices: JSON.stringify(quote.pricerLoanTerms.investorSalePrices ?? ''),
    boxes: quote.pricerLoanTerms.boxes,
    selectedBoxes: quote.pricerLoanTerms.boxes,
    rocPointsIn: quote.pointsAndFeesPercent,
    submissionInvestor: quote.pricerLoanTerms.selectedLtvRate.investorId,
    loanTerm: quote.pricerLoanTerms.loanTerm,
    duration: quote.pricerLoanTerms.loanTerm * 12,
    ficoProvidedAtOrigination: quote.ficoProvidedAtOrigination,
    loanPurpose: quote.loanPurpose,
    rentalExperienceAtOrigination: quote.rentalExperience,
    numberOfPropertiesAtSubmission: singleProperty.numberOfPropertiesAtSubmission,
    recourseStructure: singleProperty.loanRecourse,
    sfrLenderPointsIn: isInternalLender ? 0 : quote.borrowerSubmissionFees.termOriginationPoints,
    lenderPoints: isInternalLender ? 0 : quote.borrowerSubmissionFees.termOriginationPoints,
    loanSubmittedByMailId: userStore.userInformation?.username,
  }
};

export const getPrepaymentPenaltyDescription = (prePaymentPenalty) => {
  const description =
    sfrPPPMapping[prePaymentPenalty] &&
      sfrPPPMapping[prePaymentPenalty]['nValue']
      ? sfrPPPMapping[prePaymentPenalty]['nValue']
      : null;
  return description;
};